<template>
  <div class="sign-up">
    <!-- Classic Cynergy Registration Flow -->
    <div class="registration-container" :class="{ 'password-flow': tenantInfo && !tenantInfo.otpEnabled }">
      <div class="registration-card">
        <div class="card-header">
          <h1>Sign Up</h1>
          <p class="card-description">Create your Cynergy account</p>
        </div>

        <div class="card-content">
          <!-- Error Message -->
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>

          <!-- Success Message -->
          <div v-if="successMessage" class="success-message">
            {{ successMessage }}
          </div>

          <!-- Step 1: Invitation Code -->
          <div v-if="currentStep === 1" class="step-content">
            <form @submit.prevent="handleInvitationSubmit" class="invitation-form">
              <div class="form-group">
                <label for="invitation" class="form-label">
                  {{ $t('registration.invitation.code.title') }}
                </label>
                <textarea
                  id="invitation"
                  v-model="invitationCode"
                  :placeholder="$t('registration.code.input.placeholder')"
                  required
                  rows="10"
                  class="form-textarea"
                  :disabled="loading"
                />
              </div>

              <div class="form-actions">
                <button
                  type="button"
                  @click="goToLogin"
                  class="btn btn-secondary"
                  :disabled="loading"
                >
                  Back to Login
                </button>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="loading || !invitationCode.trim()"
                >
                  {{ loading ? 'Validating...' : 'Continue' }}
                </button>
              </div>
            </form>
          </div>

          <!-- Step 2: User Details -->
          <div v-if="currentStep === 2" class="step-content">
            <form @submit.prevent="handleRegistrationSubmit" class="details-form">
              <div class="form-row">
                <div class="form-group">
                  <label for="firstName" class="form-label">First Name<span class="required-asterisk">*</span></label>
                  <input
                    id="firstName"
                    v-model="userDetails.firstName"
                    type="text"
                    placeholder="First Name"
                    required
                    class="form-input"
                    :class="{ 'has-error': fieldErrors.firstName }"
                    :disabled="loading"
                    @input="fieldErrors.firstName = ''"
                    @blur="validateNameOnBlur('firstName')"
                  />
                  <p v-if="fieldErrors.firstName" class="form-error">{{ fieldErrors.firstName }}</p>
                </div>
                <div class="form-group">
                  <label for="lastName" class="form-label">Last Name<span class="required-asterisk">*</span></label>
                  <input
                    id="lastName"
                    v-model="userDetails.lastName"
                    type="text"
                    placeholder="Last Name"
                    required
                    class="form-input"
                    :class="{ 'has-error': fieldErrors.lastName }"
                    :disabled="loading"
                    @input="fieldErrors.lastName = ''"
                    @blur="validateNameOnBlur('lastName')"
                  />
                  <p v-if="fieldErrors.lastName" class="form-error">{{ fieldErrors.lastName }}</p>
                </div>
              </div>

              <div class="form-group">
                <label for="email" class="form-label">Email Address<span class="required-asterisk">*</span></label>
                <input
                  id="email"
                  v-model="userDetails.email"
                  type="email"
                  :disabled="emailFromInvitation"
                  :required="true"
                  placeholder="Enter your email address"
                  class="form-input"
                  :class="{ 'disabled': emailFromInvitation, 'has-error': fieldErrors.email }"
                  @input="fieldErrors.email = ''"
                  @blur="validateEmailOnBlur"
                />
                <p v-if="fieldErrors.email" class="form-error">{{ fieldErrors.email }}</p>
                <p v-else class="form-help">
                  {{ emailFromInvitation
                     ? 'Email from your invitation'
                     : 'Enter your email address for this domain' }}
                </p>
              </div>

              <div class="form-group">
                <label for="phoneNumber" class="form-label">Phone Number <span class="optional-badge">(Optional)</span></label>
                <div class="modern-phone-input-container">
                  <country-selector
                    v-model="selectedCountry"
                    :disabled="loading"
                    class="country-selector-modern"
                  />
                  <input
                    id="phoneNumber"
                    ref="phoneInput"
                    v-model="userDetails.phoneNumber"
                    type="tel"
                    placeholder="Enter your phone number"
                    class="form-input phone-input-modern"
                    :disabled="loading"
                    @input="onPhoneInput"
                    @blur="validatePhoneOnBlur"
                  />
                </div>
                <p class="form-help">
                  {{ selectedCountry ? `Country: ${selectedCountry.name}` : 'Select your country code' }}
                </p>
              </div>

              <div class="form-group">
                <label for="companyDomain" class="form-label">
                  Company Domain<span class="required-asterisk">*</span>
                  <span v-if="domainFromInvitation" class="domain-badge">(from invitation)</span>
                </label>
                <input
                  id="companyDomain"
                  v-model="userDetails.companyDomain"
                  type="text"
                  placeholder="example.com"
                  required
                  class="form-input"
                  :class="{ 'has-error': fieldErrors.companyDomain }"
                  :disabled="loading || domainFromInvitation"
                  @input="handleCompanyDomainChange"
                  @blur="validateCompanyDomainOnBlur"
                />
                <p v-if="fieldErrors.companyDomain" class="form-error">{{ fieldErrors.companyDomain }}</p>
                <p v-else class="form-help">
                  {{ domainFromInvitation
                     ? 'Domain is locked based on your invitation'
                     : 'Enter your company domain (e.g., example.com)' }}
                </p>
              </div>

              <!-- Password fields for classic (non-OTP) tenants -->
              <div v-if="tenantInfo && !tenantInfo.otpEnabled" class="password-section">
                <div class="form-group">
                  <label for="password" class="form-label">Password</label>
                  <input
                    id="password"
                    v-model="userDetails.password"
                    type="password"
                    placeholder="Enter your password"
                    required
                    class="form-input"
                    :disabled="loading"
                  />
                </div>
                <div class="form-group">
                  <label for="confirmPassword" class="form-label">Confirm Password</label>
                  <input
                    id="confirmPassword"
                    v-model="userDetails.confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    required
                    class="form-input"
                    :disabled="loading"
                  />
                  <p v-if="userDetails.password && userDetails.confirmPassword && userDetails.password !== userDetails.confirmPassword" class="form-error">
                    Passwords do not match
                  </p>
                </div>

                <!-- Password Requirements - Compact -->
                <div v-if="userDetails.password" class="password-requirements">
                  <div class="requirements-grid">
                    <div
                      v-for="(req, index) in passwordValidation.requirements"
                      :key="index"
                      class="requirement-item"
                      :class="{ 'met': req.met }"
                    >
                      <span class="requirement-indicator">{{ req.met ? '✓' : '○' }}</span>
                      <span class="requirement-text">{{ req.text.replace('At least ', '').replace('One ', '') }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="form-actions">
                <button
                  type="button"
                  @click="handleBack"
                  class="btn btn-secondary"
                  :disabled="loading"
                >
                  Back
                </button>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="loading || !isFormValid"
                >
                  {{ loading ? 'Creating Account...' : 'Complete Registration' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <p>{{ loading ? (currentStep === 1 ? 'Validating invitation...' : 'Creating your account...') : '' }}</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import CountrySelector from '@/components/common/CountrySelector.vue';
import { getUserLoginOptions } from '@/services/repository';
import { validatePasswordCompliance, rules } from '@/utils/validationUtils';
import { loadRecaptcha } from '@/utils/recaptcha-loader';
// Using libphonenumber-js for validation instead of vue-tel-input-vuetify
import {
  VALIDATE_INVITATION_CODE,
  UPDATE_RECAPTCHA_FOR_INVITATION_CODE,
  CHANGE_CLIENT_REGISTRATION_DATA,
  CHANGE_COMPANY_REGISTRATION_DATA,
  CHANGE_REGISTRATION_RECAPTCHA_TOKEN,
  REGISTER_USER,
  SET_REGISTRATION_USER_EMAIL,
  CLEAN_INVITATION_CODE_DATA,
  CLEAN_REGISTRATION_DATA,
} from '@/store/registration/actionTypes';

export default {
  name: 'SignUp',
  components: {
    CountrySelector,
  },
  data() {
    return {
      currentStep: 1,
      invitationCode: '',
      loading: false,
      errorMessage: '',
      successMessage: '',
      tenantInfo: {
        // Default to OTP mode for all new registrations (matches backend default)
        otpEnabled: true,
        uiMode: 'modern',
        authFlowHint: 'custom_auth',
      },
      invitationDomain: null, // Store domain from invitation for tenant checking
      selectedCountry: {
        code: 'US',
        name: 'United States',
        dialCode: '+1',
        flag: '🇺🇸',
      },
      userDetails: {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        password: '',
        confirmPassword: '',
        companyDomain: '',
      },
      emailFromInvitation: false,
      domainFromInvitation: false,
      phoneValidation: null,
      // Live blur errors per field. Empty string means valid / unset.
      fieldErrors: {
        email: '',
        firstName: '',
        lastName: '',
        companyDomain: '',
      },
    };
  },
  watch: {
    // Check tenant capabilities when email becomes available (only if we have a domain to check)
    'userDetails.email': {
      handler(newEmail) {
        if (newEmail && newEmail.includes('@') && this.invitationDomain && this.currentStep === 2) {
          this.checkTenantCapabilities(newEmail);
        }
      },
      immediate: false,
    },
  },
  computed: {
    ...mapGetters(['invitationValidationSuccessful', 'registrationClientInfo']),
    isFormValid() {
      const email = this.userDetails.email.trim();
      const domain = this.userDetails.companyDomain.trim();
      const basicFieldsValid = (
        this.userDetails.firstName.trim() &&
        this.userDetails.lastName.trim() &&
        email &&
        domain &&
        this.selectedCountry &&
        // Mirror cynergy-be's gates so users see live feedback before submit.
        rules.email(email) &&
        rules.emailNotDenied(email) &&
        rules.domain(domain) &&
        rules.companyDomainNotBlocked(domain)
      );

      // For classic tenants (non-OTP), also require password
      if (this.tenantInfo && !this.tenantInfo.otpEnabled) {
        const passwordValid = this.userDetails.password.trim() &&
                              this.userDetails.confirmPassword.trim() &&
                              this.userDetails.password === this.userDetails.confirmPassword &&
                              this.passwordValidation.isValid;
        return basicFieldsValid && passwordValid;
      }

      return basicFieldsValid;
    },
    isOtpEnabled() {
      return this.tenantInfo && this.tenantInfo.otpEnabled;
    },
    passwordValidation() {
      return validatePasswordCompliance(this.userDetails.password);
    },
  },
  async mounted() {
    // Load reCAPTCHA dynamically only for registration
    await loadRecaptcha();

    // Clean any cached registration data to ensure fresh start
    this.$store.dispatch(CLEAN_REGISTRATION_DATA);
    const urlParams = new URLSearchParams(window.location.search);
    const keyParam = urlParams.get('key');
    if (keyParam) {
      this.invitationCode = keyParam;
    }
  },
  beforeDestroy() {
    // Hide reCAPTCHA badge when leaving the page
    if (this.$recaptchaInstance) {
      this.$recaptchaInstance.hideBadge();
    }
  },
  methods: {
    async handleInvitationSubmit() {
      if (!this.invitationCode.trim()) return;

      this.loading = true;
      this.clearMessages();

      try {
        // Get recaptcha token
        await this.$recaptchaLoaded();
        const recaptchaToken = await this.$recaptcha('login');
        this.$recaptchaInstance.showBadge();

        if (!recaptchaToken) {
          throw new Error('Security validation failed. Please refresh the page and try again.');
        }

        // Validate invitation code
        await this.$store.dispatch(UPDATE_RECAPTCHA_FOR_INVITATION_CODE, recaptchaToken);

        // Parse invitation code
        let encryptedKey = this.invitationCode;
        let decodedKey = {};
        try {
          const jsonCode = Buffer.from(this.invitationCode, 'base64').toString();
          if (jsonCode.includes('encryptedKey')) {
            decodedKey = JSON.parse(jsonCode);
            encryptedKey = decodedKey?.encryptedKey;
          } else {
            encryptedKey = this.invitationCode;
          }
        } catch (parseError) {
          throw new Error('Invalid invitation code format. Please check the code and try again.');
        }

        // Validate with backend
        await this.$store.dispatch(VALIDATE_INVITATION_CODE, { code: encryptedKey });

        // Wait a moment for store to update
        await this.$nextTick();

        if (this.invitationValidationSuccessful?.valid || this.invitationValidationSuccessful?.validated) {
          // Use OTP status from invitation validation response (new format) or fallback to old format
          const validationResponse = this.invitationValidationSuccessful;

          if (validationResponse.otpEnabled !== undefined) {
            this.tenantInfo = {
              otpEnabled: validationResponse.otpEnabled,
              uiMode: validationResponse.otpEnabled ? 'modern' : 'classic',
              authFlowHint: validationResponse.otpEnabled ? 'custom_auth' : 'password_auth',
              domain: validationResponse.domain,
            };
            console.log('Using OTP status from invitation validation:', validationResponse.otpEnabled);
          } else {
            // Fallback to default OTP mode for new tenants
            this.tenantInfo = {
              otpEnabled: true,
              uiMode: 'modern',
              authFlowHint: 'custom_auth',
            };
            console.log('Using default OTP mode for new tenant');
          }

          // Extract email from invitation if available
          if (decodedKey?.email || validationResponse.email) {
            this.userDetails.email = decodedKey?.email || validationResponse.email;
            this.emailFromInvitation = true; // Mark as coming from invitation
            await this.$store.dispatch(SET_REGISTRATION_USER_EMAIL, { email: this.userDetails.email });
          } else {
            // No email in invitation - user needs to enter it
            this.userDetails.email = '';
            this.emailFromInvitation = false; // Mark as user input
          }

          // Extract domain from invitation if available and lock it
          if (validationResponse.domain) {
            this.userDetails.companyDomain = validationResponse.domain;
            this.domainFromInvitation = true; // Mark as coming from invitation - should be locked
          } else {
            // No domain in invitation - user needs to enter it
            this.userDetails.companyDomain = '';
            this.domainFromInvitation = false; // Mark as user input
          }

          this.currentStep = 2;
        } else {
          throw new Error('Invalid or expired invitation code. Please check with your administrator.');
        }
      } catch (error) {
        this.errorMessage = error.message || 'Invalid invitation code. Please check and try again.';
      } finally {
        this.loading = false;
      }
    },
    handleBack() {
      if (this.currentStep === 2) {
        this.currentStep = 1;
        this.clearMessages();
        this.domainFromInvitation = false;
        this.emailFromInvitation = false;
      }
    },
    clearMessages() {
      this.errorMessage = '';
      this.successMessage = '';
    },
    async checkTenantCapabilities(email) {
      try {
        // Only check tenant capabilities when we have email from invitation
        const response = await getUserLoginOptions(email);

        if (response.success && response.tenants && response.tenants.length > 0) {
          const emailDomain = email.split('@')[1];
          const tenant = response.tenants.find(t => t.tenant === emailDomain) || response.tenants[0];

          this.tenantInfo = {
            tenant: tenant.tenant,
            otpEnabled: tenant.otp_enabled_for_user || false,
            uiMode: tenant.otp_enabled_for_user ? 'modern' : 'classic',
            authFlowHint: tenant.auth_flow_hint || (tenant.otp_enabled_for_user ? 'custom_auth' : 'password_auth'),
          };

          console.log('Existing tenant detected from invitation email:', {
            email,
            otpEnabled: tenant.otp_enabled_for_user,
          });
        } else {
          // Default to OTP for new tenant
          this.tenantInfo = {
            otpEnabled: true,
            uiMode: 'modern',
            authFlowHint: 'custom_auth',
          };
        }
      } catch (error) {
        console.warn('Failed to check tenant capabilities, defaulting to OTP flow:', error);
        this.tenantInfo = {
          otpEnabled: true,
          uiMode: 'modern',
          authFlowHint: 'custom_auth',
        };
      }
    },

    async handleRegistrationSubmit() {
      if (!this.isFormValid) {
        this.errorMessage = 'Required fields are missing. Please fill in all required information.';
        return;
      }

      // Validate email is provided
      if (!this.userDetails.email.trim()) {
        this.errorMessage = 'Please enter a valid email address.';
        return;
      }

      // Validate phone number format (only if provided)
      if (!this.validatePhoneNumber()) {
        return; // Error message is set in validatePhoneNumber method
      }

      this.loading = true;
      this.clearMessages();

      try {
        // Route to appropriate registration flow based on tenant capabilities
        if (this.tenantInfo && this.tenantInfo.otpEnabled) {
          await this.handleModernRegistration();
        } else {
          await this.handleClassicRegistration();
        }

        // Show success and redirect - keep loading state active
        this.successMessage = 'Registration completed successfully! Redirecting to sign in...';
        // Clean up registration data and redirect
        setTimeout(async () => {
          try {
            await this.$store.dispatch(CLEAN_INVITATION_CODE_DATA);
            this.$router.replace('/');
          } catch (routerError) {
            console.error('Router navigation failed:', routerError);
            this.loading = false;
          }
        }, 1500); // Keep loading state during redirect
      } catch (error) {
        this.loading = false;
        let errorMessage = 'Registration failed. Please try again.';

        if (error.message) {
          if (error.message.includes('already exists')) {
            errorMessage = 'An account with this email already exists. Please try signing in instead.';
          } else if (error.message.includes('invitation')) {
            errorMessage = 'Invalid or expired invitation code. Please check with your administrator.';
          } else if (error.message.includes('password')) {
            errorMessage = 'Password requirements not met. Please check your password.';
          } else {
            errorMessage = error.message;
          }
        }

        this.errorMessage = errorMessage;
      }
    },

    async handleModernRegistration() {
      // Modern OTP-enabled registration (no password required)
      const fullPhoneNumber = this.userDetails.phoneNumber.trim()
        ? `${this.selectedCountry.dialCode} ${this.userDetails.phoneNumber}`
        : '';
      const emailDomain = this.userDetails.email.split('@')[1];
      const registrationData = {
        firstName: this.userDetails.firstName,
        lastName: this.userDetails.lastName,
        workEmail: this.userDetails.email, // Store expects workEmail, not email
        phoneNumber: fullPhoneNumber,
        countryCode: this.selectedCountry.code,
        cybersecurityKnowhow: { name: 'basic' },
        jobTitle: 'User',
        registrationType: 'modern',
        tenantInfo: this.tenantInfo,
      };
      await this.$store.dispatch(CHANGE_CLIENT_REGISTRATION_DATA, registrationData);
      // Set minimal company info (backend will use invitation domain and defaults)
      await this.$store.dispatch(CHANGE_COMPANY_REGISTRATION_DATA, {
        organizationDomain: emailDomain, // Backend will override with invitation domain
      });
      // Get recaptcha token for registration
      let recaptchaToken;
      try {
        await this.$recaptchaLoaded();
        recaptchaToken = await this.$recaptcha('registration');
      } catch (recaptchaError) {
        console.warn('Recaptcha failed, using skip-validation:', recaptchaError);
        recaptchaToken = 'skip-validation';
      }

      await this.$store.dispatch(CHANGE_REGISTRATION_RECAPTCHA_TOKEN, recaptchaToken);

      // Debug: Check store state before registration
      console.log('Registration store state:', {
        clientInfo: this.$store.getters.registrationClientInfo,
        companyInfo: this.$store.getters.registrationCompanyInfo,
        invitationInfo: this.$store.getters.invitationValidationSuccessful,
      });

      // Register user with modern flow
      const result = await this.$store.dispatch(REGISTER_USER);
      if (result && result.error) {
        throw new Error(result.error);
      }
      return result;
    },

    async handleClassicRegistration() {
      // Classic password-based registration
      if (!this.userDetails.password || this.userDetails.password !== this.userDetails.confirmPassword) {
        throw new Error('Password is required and must match confirmation');
      }

      // Validate password compliance
      const passwordValidation = validatePasswordCompliance(this.userDetails.password);
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.error);
      }

      const fullPhoneNumber = this.userDetails.phoneNumber.trim()
        ? `${this.selectedCountry.dialCode} ${this.userDetails.phoneNumber}`
        : '';
      const emailDomain = this.userDetails.email.split('@')[1];
      const registrationData = {
        firstName: this.userDetails.firstName,
        lastName: this.userDetails.lastName,
        workEmail: this.userDetails.email, // Store expects workEmail, not email
        password: this.userDetails.password,
        phoneNumber: fullPhoneNumber,
        countryCode: this.selectedCountry.code,
        cybersecurityKnowhow: { name: 'basic' },
        jobTitle: 'User',
        registrationType: 'classic',
        tenantInfo: this.tenantInfo,
      };

      // Update store with user details
      await this.$store.dispatch(CHANGE_CLIENT_REGISTRATION_DATA, registrationData);

      // Set minimal company info (backend will use invitation domain and defaults)
      await this.$store.dispatch(CHANGE_COMPANY_REGISTRATION_DATA, {
        organizationDomain: emailDomain, // Backend will override with invitation domain
      });

      // Get recaptcha token for registration
      let recaptchaToken;
      try {
        await this.$recaptchaLoaded();
        recaptchaToken = await this.$recaptcha('registration');
      } catch (recaptchaError) {
        console.warn('Recaptcha failed, using skip-validation:', recaptchaError);
        recaptchaToken = 'skip-validation';
      }

      await this.$store.dispatch(CHANGE_REGISTRATION_RECAPTCHA_TOKEN, recaptchaToken);

      // Debug: Check store state before registration
      console.log('Registration store state:', {
        clientInfo: this.$store.getters.registrationClientInfo,
        companyInfo: this.$store.getters.registrationCompanyInfo,
        invitationInfo: this.$store.getters.invitationValidationSuccessful,
      });

      // Register user with classic flow
      const result = await this.$store.dispatch(REGISTER_USER);
      if (result && result.error) {
        throw new Error(result.error);
      }
      return result;
    },

    onCountryChanged(country) {
      // Update selected country when user changes it in the phone input
      if (country) {
        this.selectedCountry = {
          code: country.iso2 ? country.iso2.toUpperCase() : country.code,
          name: country.name,
          dialCode: country.dialCode ? `+${country.dialCode}` : country.dialCode,
          flag: country.emoji || '🏳️',
        };
      }
    },

    onPhoneInput(event) {
      let value = event.target.value.replace(/[^0-9]/g, '');

      // Apply basic formatting based on selected country
      if (this.selectedCountry?.code === 'US' || this.selectedCountry?.code === 'CA') {
        if (value.length >= 6) {
          value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
        } else if (value.length >= 3) {
          value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
        }
      }

      this.userDetails.phoneNumber = value;
    },

    handleCompanyDomainChange(event) {
      const companyDomain = event.target.value.trim().toLowerCase();
      this.userDetails.companyDomain = companyDomain;
      if (this.fieldErrors.companyDomain) this.fieldErrors.companyDomain = '';
    },

    validateEmailOnBlur() {
      const v = (this.userDetails.email || '').trim();
      if (!v) { this.fieldErrors.email = ''; return; }
      if (!rules.email(v)) {
        this.fieldErrors.email = 'Please enter a valid email address';
      } else if (!rules.emailNotDenied(v)) {
        this.fieldErrors.email = 'Registration is not allowed for this email address';
      } else {
        this.fieldErrors.email = '';
      }
    },

    validateCompanyDomainOnBlur() {
      const v = (this.userDetails.companyDomain || '').trim();
      if (!v) { this.fieldErrors.companyDomain = ''; return; }
      if (!rules.domain(v)) {
        this.fieldErrors.companyDomain = 'Please enter a valid domain (e.g., example.com)';
      } else if (!rules.companyDomainNotBlocked(v)) {
        this.fieldErrors.companyDomain = 'Registration is not allowed for this company domain';
      } else {
        this.fieldErrors.companyDomain = '';
      }
    },

    validateNameOnBlur(field) {
      const v = (this.userDetails[field] || '').trim();
      const label = field === 'firstName' ? 'First name' : 'Last name';
      if (!v) { this.fieldErrors[field] = `${label} is required`; return; }
      if (v.length > 60) { this.fieldErrors[field] = `${label} is too long`; return; }
      this.fieldErrors[field] = '';
    },

    validatePhoneOnBlur() {
      // Clear any previous error when user finishes typing
      if (this.errorMessage && this.errorMessage.includes('phone')) {
        this.errorMessage = '';
      }
    },

    validatePhoneNumber() {
      // Phone is optional — skip validation if empty
      if (!this.userDetails.phoneNumber || this.userDetails.phoneNumber.trim() === '') {
        return true;
      }

      // Basic validation - ensure it has reasonable length if provided
      const cleanPhone = this.userDetails.phoneNumber.replace(/[^\d]/g, '');
      if (cleanPhone.length < 7) {
        this.errorMessage = 'Please enter a valid phone number.';
        return false;
      }

      return true;
    },

    goToLogin() {
      this.$store.dispatch(CLEAN_INVITATION_CODE_DATA);
      this.$router.push('/');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.sign-up {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: flex-start;
  background: transparent !important;
  padding: rem(20);
  overflow: hidden;
}

.registration-container {
  width: 100%;
  max-width: rem(600);
  margin: 0 auto;
  padding: 0;
  flex-shrink: 0;

  &.password-flow {
    max-width: rem(700);
  }
}

.registration-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(rem(20));
  border-radius: $radius-2xl;
  box-shadow: 0 rem(20) rem(40) rgba(0, 0, 0, 0.1);
  border: rem(1) solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
  width: 100%;
  height: fit-content;
  padding: rem(20) rem(24);

  &::-webkit-scrollbar-thumb {
    background: $auth-text-muted;
    border-radius: rem(3);

    &:hover {
      background: $auth-text-secondary;
    }
  }
}

.card-header {
  text-align: center;
  padding: 0;
  background: transparent;
  color: $auth-text-primary;
  border-radius: 0;

  h1 {
    font-size: rem(24);
    font-weight: 700;
    margin: 0 0 rem(4) 0;
    line-height: rem(28);
  }

  .card-description {
    font-size: rem(13);
    opacity: 0.7;
    margin: 0 0 rem(16) 0;
    color: $auth-text-secondary;
  }
}

.card-content {
  padding: 0;
  overflow: visible;
  width: 100%;
}

.step-content {
  width: 100%;
}

.error-message {
  color: $auth-error;
  background: $auth-error-bg;
  border: rem(1) solid $auth-error-border;
  border-radius: $radius-xl;
  padding: $space-3 $space-4;
  margin-bottom: $space-4;
  font-size: rem(13);
  line-height: rem(18);
  text-align: center;
  font-weight: 500;
}

.success-message {
  color: $auth-success;
  background: $auth-success-bg;
  border: rem(1) solid $auth-success-border;
  border-radius: $radius-xl;
  padding: $space-3 $space-4;
  margin-bottom: $space-4;
  font-size: rem(13);
  line-height: rem(18);
  text-align: center;
  font-weight: 500;
}

.invitation-form,
.details-form {
  display: flex;
  flex-direction: column;
  gap: rem(12);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: rem(4);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: rem(12);
}

.phone-input-container {
  display: flex;
  gap: $space-2;
}

.country-selector {
  flex-shrink: 0;
}

.phone-input {
  flex: 1;
}

.form-label {
  font-size: rem(13);
  font-weight: 600;
  color: $auth-text-primary;
  margin-bottom: rem(2);

  .required-asterisk {
    color: $auth-error;
    margin-left: rem(2);
    font-weight: 700;
  }

  .domain-badge {
    font-size: rem(10);
    color: $auth-brand;
    font-weight: 500;
    margin-left: $space-1;
  }

  .optional-badge {
    font-size: rem(11);
    color: #9ca3af;
    font-weight: 400;
    margin-left: $space-1;
  }
}

.form-input,
.form-textarea {
  width: 100%;
  padding: rem(10) rem(14);
  height: rem(42);
  border: rem(2) solid $auth-border;
  border-radius: $radius-lg;
  font-size: rem(14);
  background: $white;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: $auth-brand;
  }

  &.has-error {
    border-color: $auth-error;
    &:focus { border-color: $auth-error; }
  }

  &:disabled,
  &.disabled {
    background-color: #f8fafc;
    color: #6b7280;
    cursor: not-allowed;
    opacity: 0.7;
  }

  &::placeholder {
    color: #94a3b8;
  }
}

.form-textarea {
  height: auto;
  min-height: rem(140);
  resize: vertical;
  font-family: inherit;
  padding: $space-4 $space-5;
  font-size: $font-size-md;
  border: rem(2) solid $auth-border;
  border-radius: $radius-2xl;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: $auth-brand;
  }
}

.form-help {
  font-size: $font-size-sm;
  color: $auth-text-secondary;
  margin-top: $space-0-5;
  line-height: 1.2;
}

.form-error {
  font-size: $font-size-sm;
  color: $auth-error;
  margin-top: $space-0-5;
  line-height: 1.2;
}

.password-section {
  border-top: rem(1) solid $auth-border;
  padding-top: $space-2;
  margin-top: $space-2;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $space-2;
  grid-column: 1 / -1;

  .password-requirements {
    grid-column: 1 / -1;
    background: $auth-bg-secondary;
    border: rem(1) solid $auth-border;
    border-radius: $radius-6;
    padding: $space-2;
    margin-top: $space-2;

    .requirements-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: $space-1;
    }

    .requirement-item {
      display: flex;
      align-items: center;
      gap: $space-1;
      font-size: rem(11);

      .requirement-indicator {
        width: rem(12);
        height: rem(12);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: $radius-full;
        font-size: rem(8);
        font-weight: bold;
        flex-shrink: 0;
      }

      .requirement-text {
        color: $auth-text-secondary;
        line-height: 1.2;
      }

      &.met {
        .requirement-indicator {
          background: $auth-success;
          color: $white;
        }
        .requirement-text {
          color: #059669;
        }
      }

      &:not(.met) {
        .requirement-indicator {
          background: $auth-border;
          color: $auth-text-muted;
        }
      }
    }
  }
}

.otp-info {
  background: #f0f9ff;
  border: rem(1) solid #bae6fd;
  border-radius: $radius-6;
  padding: $space-4;
  margin-top: $space-4;

  .info-text {
    margin: 0;
    font-size: $font-size-base;
    color: #0369a1;
    display: flex;
    align-items: center;
    gap: $space-2;
  }

  .info-icon {
    font-size: rem(20);
  }
}

// Modern phone input styling to match other fields
.modern-phone-input-container {
  display: flex;
  gap: $space-1;
  width: 100%;
  position: relative;

  .country-selector-modern {
    flex-shrink: 0;
    width: rem(90);
    position: relative;
    z-index: 10;
  }

  .phone-input-modern {
    flex: 1;
    width: 100%;
    padding: rem(10) rem(14);
    height: rem(42);
    border: rem(2) solid $auth-border;
    border-radius: $radius-10;
    font-size: rem(14);
    background: $auth-bg-primary;
    transition: all 0.3s ease;

    &:focus {
      outline: none;
      border-color: $auth-brand;
    }

    &:disabled {
      background-color: $auth-bg-secondary;
      color: $auth-text-secondary;
      cursor: not-allowed;
      opacity: 0.7;
    }

    &::placeholder {
      color: $auth-text-muted;
    }
  }
}

.form-actions {
  display: flex;
  gap: rem(12);
  margin-top: $space-2;
  justify-content: center;
  align-items: center;
}

.btn {
  flex: 0 1 auto;
  min-width: rem(128);
  padding: $space-2 $space-6;
  border: none;
  border-radius: $radius-xl;
  font-size: $font-size-md;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  height: rem(48);

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &.btn-primary {
    background: linear-gradient(135deg, $auth-gradient-start 0%, $auth-gradient-end 100%);
    color: $white;
    box-shadow: 0 rem(3) rem(8) $auth-brand-shadow;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, $auth-brand-hover 0%, #9333ea 100%);
      transform: translateY(rem(-1));
      box-shadow: 0 rem(4) rem(12) $auth-brand-shadow-hover;
    }
  }

  &.btn-secondary {
    background: linear-gradient(135deg, $auth-bg-primary 0%, $auth-bg-secondary 100%);
    color: $auth-text-secondary;
    border: rem(2) solid $auth-border;

    &:hover:not(:disabled) {
      background: linear-gradient(135deg, $auth-bg-secondary 0%, $auth-bg-tertiary 100%);
      border-color: $auth-border-hover;
      box-shadow: 0 rem(2) rem(8) $auth-card-shadow;
    }
  }
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: $z-modal;
  color: #ffffff !important;

  .loading-spinner {
    width: rem(40);
    height: rem(40);
    border: rem(4) solid rgba(255, 255, 255, 0.3);
    border-top: rem(4) solid $white;
    border-radius: $radius-full;
    animation: spin 1s linear infinite;
    margin-bottom: $space-4;
  }

  p {
    font-size: $font-size-md;
    margin: 0;
    color: #ffffff !important;
  }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
